import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styles from './IslamicETFPanel.module.css';

/*
 * Curated list of Islamic ETFs and halal commodity ETFs.
 * Certified = purpose-built and supervised by a Shariah board.
 * Commodity = physical gold/silver, broadly accepted as halal.
 */
const ETF_LIST = [
  // ── Shariah-Certified Equity ETFs ──────────────────────────────
  {
    ticker:  'HLAL',
    name:    'Wahed FTSE USA Shariah ETF',
    focus:   'US Large Cap',
    manager: 'Wahed Invest',
    category: 'equity',
    note:    'Tracks the FTSE USA Shariah Index. Certified by Amanie Advisors.',
  },
  {
    ticker:  'SPUS',
    name:    'SP Funds S&P 500 Shariah ETF',
    focus:   'S&P 500 Filtered',
    manager: 'SP Funds',
    category: 'equity',
    note:    'S&P 500 companies after removing prohibited sectors.',
  },
  {
    ticker:  'SPRE',
    name:    'SP Funds Emerging Markets Shariah',
    focus:   'Emerging Markets',
    manager: 'SP Funds',
    category: 'equity',
    note:    'Broad EM exposure screened to AAOIFI standards.',
  },
  {
    ticker:  'SPTE',
    name:    'SP Funds Technology Shariah ETF',
    focus:   'Global Technology',
    manager: 'SP Funds',
    category: 'equity',
    note:    'Technology sector companies passing Shariah criteria.',
  },
  {
    ticker:  'SPHE',
    name:    'SP Funds Healthcare Shariah ETF',
    focus:   'Global Healthcare',
    manager: 'SP Funds',
    category: 'equity',
    note:    'Healthcare companies screened for Shariah compliance.',
  },
  {
    ticker:  'UMMA',
    name:    'Wahed Dow Jones Islamic World ETF',
    focus:   'Global Equities',
    manager: 'Wahed Invest',
    category: 'equity',
    note:    'Global Islamic equity exposure across developed markets.',
  },
  // ── Shariah-Certified Fixed Income (Sukuk) ──────────────────────
  // {
  //   ticker:  'SUKUK',
  //   name:    'SP Funds Dow Jones Global Sukuk ETF',
  //   focus:   'Islamic Bonds',
  //   manager: 'SP Funds',
  //   category: 'sukuk',
  //   note:    'Sukuk (Islamic bonds) — interest-free fixed-income alternative.',
  // },
  // // ── Halal Commodity ETFs ────────────────────────────────────────
  // {
  //   ticker:  'IAU',
  //   name:    'iShares Gold Trust',
  //   focus:   'Physical Gold',
  //   manager: 'BlackRock',
  //   category: 'commodity',
  //   note:    'Backed 1:1 by physical gold. Gold is a consensus halal asset.',
  // },
  // {
  //   ticker:  'GLD',
  //   name:    'SPDR Gold Shares',
  //   focus:   'Physical Gold',
  //   manager: 'State Street',
  //   category: 'commodity',
  //   note:    "World's largest gold ETF by AUM. Physical gold backing.",
  // },
  // {
  //   ticker:  'SLV',
  //   name:    'iShares Silver Trust',
  //   focus:   'Physical Silver',
  //   manager: 'BlackRock',
  //   category: 'commodity',
  //   note:    'Physical silver holding. Silver is widely considered halal.',
  // },
];


const CATEGORY_ORDER = ['equity', 'sukuk', 'commodity'];

function groupByCategory(list) {
  return CATEGORY_ORDER.map(cat => ({
    cat,
    items: list.filter(e => e.category === cat),
  })).filter(g => g.items.length > 0);
}

export default function IslamicETFPanel({ onScreen }) {
  const { t } = useTranslation();
  const [prices, setPrices] = useState({});

  // Fetch live quotes for all tickers in one shot
  useEffect(() => {
    const tickers = ETF_LIST.map(e => e.ticker).join(',');
    axios.get(`/api/quotes?symbols=${tickers}`)
      .then(({ data }) => setPrices(data || {}))
      .catch(() => {}); // prices are decorative — silently skip on error
  }, []);

  const groups = groupByCategory(ETF_LIST);

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.label}>{t('islamic_etf.label')}</span>
          <span className={styles.count}>{ETF_LIST.length}</span>
        </div>
        <p className={styles.sub}>{t('islamic_etf.subtitle')}</p>
      </div>

      {groups.map(({ cat, items }) => (
        <div key={cat} className={styles.group}>
          <p className={styles.groupLabel}>{t(`islamic_etf.categories.${cat}`)}</p>
          <div className={styles.grid}>
            {items.map(etf => {
              const q   = prices[etf.ticker];
              const pct = q?.changePct;
              return (
                <div
                  key={etf.ticker}
                  className={`${styles.card} ${styles['card_' + etf.category]}`}
                  onClick={() => onScreen(etf.ticker)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && onScreen(etf.ticker)}
                >
                  <div className={styles.cardTop}>
                    <span className={styles.ticker}>{etf.ticker}</span>
                    {q?.price
                      ? <span className={styles.price}>${q.price.toFixed(2)}</span>
                      : <span className={styles.screenCta}>{t('islamic_etf.screen_cta')}</span>
                    }
                  </div>

                  <p className={styles.name}>{etf.name}</p>
                  <p className={styles.note}>{etf.note}</p>

                  <div className={styles.cardBottom}>
                    <span className={`${styles.focusBadge} ${styles['focus_' + etf.category]}`}>
                      {etf.focus}
                    </span>
                    {pct != null && (
                      <span className={`${styles.change} ${pct >= 0 ? styles.changePos : styles.changeNeg}`}>
                        {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <p className={styles.disclaimer}>{t('islamic_etf.disclaimer')}</p>
    </div>
  );
}
